import { IconButton, Menu, MenuItem, makeStyles } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import throttle from 'lodash/throttle';
import React, { SyntheticEvent, useState, useRef, useEffect, useCallback, ReactNode } from 'react';

import { Colors } from 'src/common/ui/base';

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  menuList: {
    backgroundColor: Colors.Ocean,
    pointerEvents: 'auto',
    padding: 0,

    '& li': {
      backgroundColor: Colors.Ocean,
      color: 'white',
      width: '173px',
      height: '37px',
      padding: '10px',
      fontFamily: '"Nunito", "Nunito Sans"',
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '17.4px',

      '&:not(:first-child)': {
        borderTop: `1px solid rgba(255,255,255,0.15)`,
      },

      '&:hover': {
        backgroundColor: Colors.BlueSpruce,
      },

      '&.Mui-disabled': {
        backgroundColor: Colors.CoolGray7,
        color: Colors.CoolGray5,
        cursor: 'not-allowed',
        opacity: 1,
      },
    },
  },
  menuPaper: {
    borderRadius: 0,
  },
  popover: {
    pointerEvents: 'none',
  },
  iconButton: {
    padding: 0,
    top: -2,
    '& svg': {
      transform: 'scale(0.66)',
    },
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
});

// check if mouse is hovering element
const checkBound = (event: MouseEvent, element: HTMLElement): boolean => {
  if (!element) return false;

  const rect = element.getBoundingClientRect();

  if (
    event.clientX < rect.left ||
    event.clientX > rect.right ||
    event.clientY < rect.top ||
    event.clientY > rect.bottom
  )
    return false;

  return true;
};

export interface ButtonToDropdownMenuItemConfig {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface ButtonToDropdownMenuProps {
  menuItems: ButtonToDropdownMenuItemConfig[];
  icon?: ReactNode;
  testId?: string;
  onMenuStateChange?: (isOpen: boolean) => void;
  onMenuExited?: () => void;
}

/**
 * A reusable hover-activated menu component
 *
 * Features:
 * - Opens on hover, closes when mouse leaves menu and button area
 * - Configurable menu items with labels, click handlers, and disabled states
 * - Customizable icon and test IDs
 * - Callback support for menu state changes and exit events
 * - Consistent styling with the application theme
 */
export const ButtonToDropdownMenu: React.FC<ButtonToDropdownMenuProps> = ({
  menuItems,
  onMenuStateChange,
  onMenuExited,
  icon = <MoreVertIcon className="three-dot-icon" />,
  testId = 'hover-menu',
}) => {
  const styles = useStyles();

  const menuButtonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClose = () => {
    setAnchorEl(null);
    window.removeEventListener('mousemove', handleMouseMove);
    onMenuStateChange?.(false);
  };

  const handleOpen = (event: SyntheticEvent<HTMLButtonElement>) => {
    if (anchorEl) return;
    setAnchorEl(event.currentTarget);
    window.addEventListener('mousemove', handleMouseMove);
    onMenuStateChange?.(true);
  };

  // listen to mouse movement for hover state
  const handleMouseMove = useCallback(
    // using mousemove event over onMouseLeave for more solid behavior
    throttle((event: MouseEvent) => {
      const hoverMenuButton = checkBound(event, menuButtonRef.current as HTMLElement);
      const hoverMenu = checkBound(event, menuRef.current as HTMLElement);

      if (!hoverMenuButton && !hoverMenu) handleClose();
    }, 100),
    [menuRef, menuButtonRef, anchorEl],
  );

  // stop listening when menu closed
  useEffect(() => {
    if (!anchorEl) {
      window.removeEventListener('mousemove', handleMouseMove);
    }
  }, [anchorEl]);

  // cleanup on component unmount
  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div className={styles.wrapper} ref={menuButtonRef} data-testid={testId}>
      <IconButton
        classes={{ root: styles.iconButton }}
        data-testid={`${testId}-toggle`}
        onMouseEnter={handleOpen}
        onMouseMove={handleOpen}
        tabIndex={-1}
      >
        {icon}
      </IconButton>
      <Menu
        MenuListProps={{
          classes: { root: styles.menuList },
          ref: menuRef,
        }}
        PopoverClasses={{ root: styles.popover }}
        TransitionProps={{
          onExited: onMenuExited,
        }}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoFocus={false}
        classes={{ paper: styles.menuPaper }}
        container={menuButtonRef.current}
        disableAutoFocusItem
        getContentAnchorEl={null}
        id="hover-menu"
        keepMounted={false}
        open={!!anchorEl}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {menuItems.map(item => (
          <MenuItem
            key={item.label}
            disabled={item.disabled}
            onClick={() => {
              handleClose();
              item.onClick();
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};
